import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Receipt, FileText, Palette, Camera, Code, PenTool, Check, ArrowRight, Clock, DollarSign, BarChart3 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const FreelancersLanding = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Free Invoice Generator for Freelancers | FinScan</title>
        <meta name="description" content="Send professional invoices in seconds. Free invoice generator for freelancers — designers, photographers, developers, writers. Get paid faster with FinScan." />
        <link rel="canonical" href="https://finscan.store/freelancers" />
        <meta property="og:title" content="Free Invoice Generator for Freelancers | FinScan" />
        <meta property="og:description" content="Send professional invoices in seconds. Free for designers, photographers, developers, writers." />
        <meta property="og:url" content="https://finscan.store/freelancers" />
      </Helmet>

      <div className="min-h-screen bg-[#030712]">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center space-x-2">
                <Receipt className="w-8 h-8 text-indigo-400" />
                <span className="text-2xl font-bold font-['Outfit'] bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400">
                  FinScan
                </span>
              </Link>
              <div className="flex items-center space-x-4">
                <Link to="/blog" className="text-muted-foreground hover:text-white transition-colors hidden sm:block">
                  Blog
                </Link>
                <button
                  onClick={() => navigate('/auth')}
                  className="bg-white text-black hover:bg-white/90 font-medium px-4 py-2 rounded-full transition-all"
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-purple-500/10 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center justify-center gap-4 mb-6">
                <Palette className="w-8 h-8 text-pink-400" />
                <Camera className="w-8 h-8 text-purple-400" />
                <Code className="w-8 h-8 text-blue-400" />
                <PenTool className="w-8 h-8 text-indigo-400" />
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold font-['Outfit'] tracking-tight mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-200 to-purple-400">
                  Get Paid Faster.
                </span>
                <br />
                <span className="text-white/80 text-3xl md:text-5xl">Look More Professional.</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
                Create beautiful invoices in 30 seconds. Track every expense. Focus on your work, not the paperwork.
              </p>

              <button
                onClick={() => navigate('/auth')}
                data-testid="freelancers-cta-hero"
                className="bg-purple-500 text-white hover:bg-purple-400 font-semibold px-8 py-4 rounded-full shadow-[0_0_30px_rgba(168,85,247,0.4)] transition-all transform hover:scale-105 active:scale-95 text-lg"
              >
                Create Your First Invoice Free
              </button>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gradient-to-b from-transparent to-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="p-8 bg-white/5 border border-white/10 rounded-2xl"
              >
                <FileText className="w-10 h-10 text-purple-400 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Beautiful Invoices</h3>
                <p className="text-muted-foreground">
                  Professional templates with your logo and brand colors. Send as PDF or payment link.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="p-8 bg-white/5 border border-white/10 rounded-2xl"
              >
                <Receipt className="w-10 h-10 text-purple-400 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Track Project Expenses</h3>
                <p className="text-muted-foreground">
                  Software subscriptions, equipment, client meals — scan receipts and track it all.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="p-8 bg-white/5 border border-white/10 rounded-2xl"
              >
                <BarChart3 className="w-10 h-10 text-purple-400 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Tax Time = 5 Minutes</h3>
                <p className="text-muted-foreground">
                  All expenses categorized automatically. Export to PDF or CSV for your accountant.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold font-['Outfit'] text-center mb-12">
              How It Works
            </h2>
            
            <div className="space-y-8">
              {[
                { step: '1', title: 'Add your client and services', desc: 'Takes 30 seconds', icon: Clock },
                { step: '2', title: 'Send the invoice via email or link', desc: 'One click delivery', icon: ArrowRight },
                { step: '3', title: 'Get paid — track what\'s paid and what\'s overdue', desc: 'Full visibility', icon: DollarSign },
              ].map(({ step, title, desc, icon: Icon }, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="flex items-center gap-6 p-6 bg-white/5 border border-white/10 rounded-2xl"
                >
                  <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-purple-400">{step}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">{title}</h3>
                    <p className="text-muted-foreground">{desc}</p>
                  </div>
                  <Icon className="w-6 h-6 text-purple-400 hidden sm:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-20 bg-gradient-to-b from-white/5 to-transparent">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="p-8 bg-gradient-to-br from-purple-500/10 via-transparent to-transparent border border-purple-500/20 rounded-2xl">
              <p className="text-2xl md:text-3xl font-light italic mb-6 text-white/90">
                "As a freelance photographer, I send 10-15 invoices a month. FinScan cut my admin time in half."
              </p>
              <p className="text-purple-400 font-semibold">
                — Sarah K., Freelance Photographer
              </p>
            </div>
          </div>
        </section>

        {/* Features List */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold font-['Outfit'] text-center mb-12">
              Perfect For Creative Professionals
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                'Designers & Illustrators',
                'Photographers & Videographers',
                'Writers & Content Creators',
                'Developers & Consultants',
                'Social Media Managers',
                'Marketing Freelancers'
              ].map((profession, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  className="flex items-center gap-3 p-4 bg-white/5 rounded-xl"
                >
                  <Check className="w-5 h-5 text-purple-400 flex-shrink-0" />
                  <span>{profession}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-6">
              Join 10,000+ freelancers. Start free today.
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              No credit card required. Set up in 60 seconds.
            </p>
            <button
              onClick={() => navigate('/auth')}
              data-testid="freelancers-cta-final"
              className="bg-purple-500 text-white hover:bg-purple-400 font-semibold px-10 py-5 rounded-full shadow-[0_0_30px_rgba(168,85,247,0.4)] transition-all transform hover:scale-105 text-xl"
            >
              Create Your Free Account
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-muted-foreground text-sm">
                © 2026 FinScan. Built for creative professionals.
              </p>
              <div className="flex gap-6 text-sm">
                <Link to="/electricians" className="text-muted-foreground hover:text-white transition-colors">Contractors</Link>
                <Link to="/1099" className="text-muted-foreground hover:text-white transition-colors">1099 Workers</Link>
                <Link to="/blog" className="text-muted-foreground hover:text-white transition-colors">Blog</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default FreelancersLanding;
